import {
  AbilityBuilder,
  MongoQuery,
  buildMongoQueryMatcher,
  createMongoAbility,
} from '@casl/ability';
import { $and, and } from '@ucast/mongo2js';
import { Effect } from '../entity';
import { IJwtValidatedUser } from '../req';
import {
  BooleanOperator,
  BooleanSingleValue,
  Condition,
  NumberOperator,
  NumberSingleValue,
  OneBooleanOperatorKey,
  OneNumberOperatorKey,
  OneStringOperatorKey,
  PolicyConditionType,
  StringOperator,
  StringSingleValue,
} from './casl.models';

const createPolicyCondition = (
  type: PolicyConditionType,
  field: string,
  value: string | number | boolean
) => {
  switch (type) {
    case 'Equals':
      return { [field]: { $eq: value } };
    case 'NotEquals':
      return { [field]: { $ne: value } };
    case 'LessThan':
      return {
        $and: [{ [field]: { $exists: true } }, { [field]: { $lt: value } }],
      };
    case 'LessThanEquals':
      return {
        $and: [{ [field]: { $exists: true } }, { [field]: { $lte: value } }],
      };
    case 'GreaterThan':
      return { [field]: { $gt: value } };
    case 'GreaterThanEquals':
      return { [field]: { $gte: value } };
  }
};

export function withUser(user: IJwtValidatedUser) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);
  const policies = user.policies;

  if (policies) {
    policies.forEach((p) => {
      const actions = p.actions;
      const subject = p.scope;
      actions.forEach((action: string) => {
        if (action === undefined || subject === undefined) return;
        const tran = getActionAndSubject(action);
        if (p.effect === Effect.Allow) {
          can(tran, subject);
        } else {
          cannot(tran, subject);
        }
      });
    });
  }

  return build({
    conditionsMatcher: buildMongoQueryMatcher({ $and }, { and }),
  });
}

function getActionAndSubject(actionString: string): string {
  let action = actionString;
  if (actionString === '*') {
    action = 'manage';
  }
  return action;
}

export function queryCondition(user: IJwtValidatedUser) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);
  const policies = user.policies;

  if (policies) {
    policies.forEach((p) => {
      const actions = p.actions;
      actions.forEach((a) => {
        const action = getActionAndSubject(a);
        const subject = p.scope;
        if (action === undefined || subject === undefined) return;

        // Query out specific resource
        const all = p.resources.includes('*');
        let conditions: MongoQuery | undefined = all
          ? undefined
          : { id: { $in: p.resources } };

        // check condition
        if (p.condition != null) {
          const con = p.condition as unknown as Condition;
          const operator = Object.keys(con)[0];
          let condition;
          let policyCondition;

          switch (operator) {
            case 'StringEquals':
            case 'StringNotEquals': {
              condition = con as OneStringOperatorKey<
                StringOperator,
                StringSingleValue
              >;

              const op = condition[operator];
              let key: unknown;
              let value: unknown;

              if (op) {
                key = Object.keys(op)[0];
                value = Object.values(op)[0];
              }

              const policyType = operator.replace(
                'String',
                ''
              ) as PolicyConditionType;

              let valueString = value as string;
              switch (valueString) {
                case '$user.email':
                  valueString = user.sub;
                  break;
              }

              policyCondition = createPolicyCondition(
                policyType,
                key as string,
                valueString
              );
              break;
            }
            case 'NumberEquals':
            case 'NumberNotEquals':
            case 'NumberLessThan':
            case 'NumberLessThanEquals':
            case 'NumberGreaterThan':
            case 'NumberGreaterThanEquals': {
              condition = con as OneNumberOperatorKey<
                NumberOperator,
                NumberSingleValue
              >;
              const op = condition[operator];
              let key: unknown;
              let value: unknown;

              if (op) {
                key = Object.keys(op)[0];
                value = Object.values(op)[0];
              }

              const policyType = operator.replace(
                'Number',
                ''
              ) as PolicyConditionType;

              policyCondition = createPolicyCondition(
                policyType,
                key as string,
                value as number
              );

              break;
            }
            case 'Bool': {
              condition = con as OneBooleanOperatorKey<
                BooleanOperator,
                BooleanSingleValue
              >;
              const op = condition[operator];
              let key: unknown;
              let value: unknown;

              if (op) {
                key = Object.keys(op)[0];
                value = Object.values(op)[0];
              }

              const policyType = operator.replace(
                'Equals',
                ''
              ) as PolicyConditionType;

              policyCondition = createPolicyCondition(
                policyType,
                key as string,
                value as number
              );
              break;
            }
          }
          conditions = { ...conditions, ...policyCondition };
        }

        if (p.effect == Effect.Allow) {
          can(action, subject, conditions);
        } else {
          cannot(action, subject, conditions);
        }
      });
    });
  }

  return build({
    conditionsMatcher: buildMongoQueryMatcher({ $and }, { and }),
  });
}
